import React, { useMemo, useState } from 'react';
import { PageHeader } from '../../components/molecules';

interface User {
  id: string;
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  role: 'admin' | 'veterinarian' | 'receptionist' | 'technician';
  phone: string;
  isActive: boolean;
  lastLogin: string;
  createdAt: string;
  avatar?: string;
}

const roles = [
  { value: 'admin', label: 'Administrator' },
  { value: 'veterinarian', label: 'Veterinarian' },
  { value: 'receptionist', label: 'Receptionist' },
  { value: 'technician', label: 'Technician' }
] as const;

const roleStyles: Record<User['role'], { badge: string; label: string }> = {
  admin: {
    badge: 'bg-rose-100 text-rose-600 border border-rose-200',
    label: 'Administrator'
  },
  veterinarian: {
    badge: 'bg-emerald-100 text-emerald-600 border border-emerald-200',
    label: 'Veterinarian'
  },
  receptionist: {
    badge: 'bg-sky-100 text-sky-600 border border-sky-200',
    label: 'Receptionist'
  },
  technician: {
    badge: 'bg-amber-100 text-amber-600 border border-amber-200',
    label: 'Technician'
  }
};

const Users: React.FC = () => {
  const [users, setUsers] = useState<User[]>([
    {
      id: '1',
      username: 'admin',
      email: 'admin@vetclinic.com',
      firstName: 'John',
      lastName: 'Administrator',
      role: 'admin',
      phone: '+1-555-0101',
      isActive: true,
      lastLogin: '2024-01-15 09:30',
      createdAt: '2023-01-01'
    },
    {
      id: '2',
      username: 'sjohnson',
      email: 'sarah.johnson@vetclinic.com',
      firstName: 'Sarah',
      lastName: 'Johnson',
      role: 'veterinarian',
      phone: '+1-555-0102',
      isActive: true,
      lastLogin: '2024-01-15 08:15',
      createdAt: '2023-02-15'
    },
    {
      id: '3',
      username: 'mchen',
      email: 'michael.chen@vetclinic.com',
      firstName: 'Michael',
      lastName: 'Chen',
      role: 'veterinarian',
      phone: '+1-555-0103',
      isActive: true,
      lastLogin: '2024-01-14 16:45',
      createdAt: '2023-03-20'
    },
    {
      id: '4',
      username: 'jwilson',
      email: 'jennifer.wilson@vetclinic.com',
      firstName: 'Jennifer',
      lastName: 'Wilson',
      role: 'receptionist',
      phone: '+1-555-0104',
      isActive: true,
      lastLogin: '2024-01-15 07:45',
      createdAt: '2023-04-10'
    },
    {
      id: '5',
      username: 'drodriguez',
      email: 'david.rodriguez@vetclinic.com',
      firstName: 'David',
      lastName: 'Rodriguez',
      role: 'technician',
      phone: '+1-555-0105',
      isActive: false,
      lastLogin: '2024-01-10 14:20',
      createdAt: '2023-05-25'
    }
  ]);

  const [showForm, setShowForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');

  const [formData, setFormData] = useState({
    username: '',
    email: '',
    firstName: '',
    lastName: '',
    role: 'receptionist' as User['role'],
    phone: '',
    password: '',
    confirmPassword: ''
  });

  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  const filteredUsers = useMemo(
    () =>
      users.filter((user) => {
        const matchesSearch =
          user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
          user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
          user.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          user.lastName.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesRole = !roleFilter || user.role === roleFilter;
        const matchesStatus =
          statusFilter === 'all' ||
          (statusFilter === 'active' && user.isActive) ||
          (statusFilter === 'inactive' && !user.isActive);
        return matchesSearch && matchesRole && matchesStatus;
      }),
    [users, searchTerm, roleFilter, statusFilter]
  );

  const roleStats = useMemo(
    () =>
      roles.map((role) => ({
        label: role.label,
        count: users.filter((user) => user.role === role.value).length
      })),
    [users]
  );

  const activeUserCount = useMemo(() => users.filter((user) => user.isActive).length, [users]);

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.firstName.trim()) {
      errors.firstName = 'Please provide a first name.';
    }
    if (!formData.lastName.trim()) {
      errors.lastName = 'Please provide a last name.';
    }
    if (!formData.username.trim()) {
      errors.username = 'Username is required.';
    } else if (formData.username.length < 3) {
      errors.username = 'Username must have at least 3 characters.';
    }
    if (!formData.email.trim()) {
      errors.email = 'Email is required.';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Email format is invalid.';
    }
    if (!formData.phone.trim()) {
      errors.phone = 'Phone number is required.';
    }
    if (!formData.password) {
      errors.password = 'Please create a password.';
    } else if (formData.password.length < 6) {
      errors.password = 'Password must have at least 6 characters.';
    }
    if (!formData.confirmPassword) {
      errors.confirmPassword = 'Confirm your password.';
    } else if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match.';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }

    const newUser: User = {
      id: Date.now().toString(),
      username: formData.username,
      email: formData.email,
      firstName: formData.firstName,
      lastName: formData.lastName,
      role: formData.role,
      phone: formData.phone,
      isActive: true,
      lastLogin: 'Never',
      createdAt: new Date().toISOString().split('T')[0]
    };

    setUsers((prev) => [...prev, newUser]);
    setFormData({
      username: '',
      email: '',
      firstName: '',
      lastName: '',
      role: 'receptionist',
      phone: '',
      password: '',
      confirmPassword: ''
    });
    setFormErrors({});
    setShowForm(false);
  };

  const handleToggleStatus = (id: string) => {
    setUsers((prev) =>
      prev.map((user) => (user.id === id ? { ...user, isActive: !user.isActive } : user))
    );
  };

  const handleDelete = (id: string) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      setUsers((prev) => prev.filter((user) => user.id !== id));
    }
  };

  const filtersActive = Boolean(searchTerm) || Boolean(roleFilter) || statusFilter !== 'all';
  const filterSummary = filtersActive
    ? [
        searchTerm ? `Keyword: "${searchTerm}"` : null,
        roleFilter ? `Role: ${roles.find((role) => role.value === roleFilter)?.label}` : null,
        statusFilter !== 'all' ? `Status: ${statusFilter}` : null
      ]
        .filter(Boolean)
        .join(' · ')
    : 'Whole team in view';

  return (
    <div className="space-y-8">
      <PageHeader
        eyebrow="Team"
        title="Team members"
        description="Manage clinic access, roles, and activity for each member of your staff."
        icon="👥"
        accent="cyan"
        actions={(
          <button
            className="inline-flex items-center gap-2 rounded-full bg-brand-primary px-5 py-2.5 text-sm font-semibold text-white shadow-soft transition hover:bg-brand-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-accent"
            onClick={() => setShowForm(true)}
          >
            <span className="text-lg leading-none">➕</span>
            Invite user
          </button>
        )}
        stats={[
          { label: 'Total users', value: users.length, helper: 'Across roles' },
          { label: 'Active users', value: activeUserCount, helper: 'Enabled accounts' },
          { label: 'Inactive users', value: users.length - activeUserCount, helper: 'Needs review' }
        ]}
      >
        <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
          <label className="flex w-full flex-col gap-2 text-sm font-semibold text-slate-600">
            Search team
            <input
              type="text"
              placeholder="Search by username, email, or name…"
              className="mt-2 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700 placeholder:text-slate-400 focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/20"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </label>
          <div className="grid gap-4 md:grid-cols-2">
            <label className="flex flex-col gap-2 text-sm font-semibold text-slate-600">
              Role
              <select
                className="mt-2 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700 focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/20"
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
              >
                <option value="">All roles</option>
                {roles.map((role) => (
                  <option key={role.value} value={role.value}>
                    {role.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-2 text-sm font-semibold text-slate-600">
              Status
              <select
                className="mt-2 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700 focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/20"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}
              >
                <option value="all">All statuses</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </label>
          </div>
        </div>
        <div className="mt-6 grid gap-2 rounded-2xl border border-slate-200 bg-white/60 p-4 text-sm text-slate-600">
          <p className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-400">Role coverage</p>
          <ul className="space-y-1">
            {roleStats.map((stat) => (
              <li key={stat.label} className="flex items-center justify-between">
                <span>{stat.label}</span>
                <span className="font-semibold text-slate-700">{stat.count}</span>
              </li>
            ))}
          </ul>
        </div>
        <p className="text-xs text-slate-500">{filterSummary}</p>
      </PageHeader>

      <section className="space-y-4">
        {filteredUsers.length === 0 ? (
          <div className="rounded-3xl border border-dashed border-brand-muted bg-white/70 px-8 py-16 text-center shadow-soft">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-brand-primary/10 text-3xl text-brand-primary">
              🧑‍⚕️
            </div>
            <h3 className="mt-6 text-lg font-semibold text-slate-700">No users match your filters</h3>
            <p className="mt-2 text-sm text-slate-500">Adjust filters or invite a new teammate.</p>
          </div>
        ) : (
          <div className="grid gap-4 xl:grid-cols-2">
            {filteredUsers.map((user) => {
              const role = roleStyles[user.role];
              return (
                <article
                  key={user.id}
                  className="flex h-full flex-col gap-6 rounded-3xl border border-slate-200 bg-white p-6 shadow-soft transition hover:-translate-y-1 hover:shadow-xl"
                >
                  <header className="flex items-start justify-between gap-4">
                    <div className="flex items-center gap-4">
                      <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-primary/10 text-lg font-semibold text-brand-primary">
                        {`${user.firstName.charAt(0)}${user.lastName.charAt(0)}`}
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-slate-800">
                          {user.firstName} {user.lastName}
                        </h3>
                        <p className="text-sm text-slate-500">@{user.username}</p>
                      </div>
                    </div>
                    <span className={`rounded-full px-3 py-1 text-xs font-semibold ${role.badge}`}>
                      {role.label}
                    </span>
                  </header>

                  <div className="grid gap-4 rounded-2xl border border-slate-100 bg-slate-50/80 p-4 text-sm">
                    <div className="space-y-1 text-slate-600">
                      <p className="font-semibold text-slate-700">{user.email}</p>
                      <p>{user.phone}</p>
                    </div>
                    <div className="grid grid-cols-2 gap-3 text-xs font-semibold uppercase tracking-[0.3em] text-slate-400">
                      <div>
                        <p>Last login</p>
                        <p className="mt-2 text-sm normal-case tracking-normal text-slate-600">
                          {user.lastLogin}
                        </p>
                      </div>
                      <div>
                        <p>Joined</p>
                        <p className="mt-2 text-sm normal-case tracking-normal text-slate-600">
                          {new Date(user.createdAt).toLocaleDateString('en-US')}
                        </p>
                      </div>
                    </div>
                  </div>

                  <footer className="flex flex-wrap items-center gap-2">
                    <span
                      className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-semibold ${
                        user.isActive
                          ? 'bg-emerald-50 text-emerald-600 border border-emerald-200'
                          : 'bg-rose-50 text-rose-600 border border-rose-200'
                      }`}
                    >
                      <span className="text-base">{user.isActive ? '●' : '○'}</span>
                      {user.isActive ? 'Active' : 'Inactive'}
                    </span>
                    <button
                      className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-xs font-semibold transition ${
                        user.isActive
                          ? 'border-amber-200 bg-amber-50 text-amber-600 hover:bg-amber-100'
                          : 'border-emerald-200 bg-emerald-50 text-emerald-600 hover:bg-emerald-100'
                      }`}
                      onClick={() => handleToggleStatus(user.id)}
                    >
                      {user.isActive ? 'Deactivate' : 'Activate'}
                    </button>
                    <button
                      className="inline-flex items-center gap-2 rounded-full border border-rose-200 bg-rose-50 px-4 py-2 text-xs font-semibold text-rose-600 transition hover:bg-rose-100"
                      onClick={() => handleDelete(user.id)}
                    >
                      Delete
                    </button>
                  </footer>
                </article>
              );
            })}
          </div>
        )}
      </section>

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 backdrop-blur-sm">
          <div className="relative w-full max-w-2xl rounded-3xl border border-slate-200 bg-white p-8 shadow-soft">
            <button
              className="absolute right-6 top-6 flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 text-lg text-slate-500 transition hover:bg-slate-100"
              onClick={() => setShowForm(false)}
              aria-label="Close form"
            >
              ✕
            </button>

            <header>
              <h2 className="text-xl font-semibold text-slate-800">Invite a team member</h2>
              <p className="mt-2 text-sm text-slate-500">
                Fill in user details and assign a role. An invitation email will be sent.
              </p>
            </header>

            <form className="mt-6 space-y-4 text-sm" onSubmit={handleSubmit} noValidate>
              <div className="grid gap-4 md:grid-cols-2">
                <label className="flex flex-col gap-2 font-semibold text-slate-600">
                  First name *
                  <input
                    type="text"
                    className={`rounded-2xl border bg-slate-50 px-4 py-3 text-slate-700 focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/20 ${
                      formErrors.firstName ? 'border-rose-300' : 'border-slate-200'
                    }`}
                    value={formData.firstName}
                    onChange={(e) => setFormData((prev) => ({ ...prev, firstName: e.target.value }))}
                  />
                  {formErrors.firstName && (
                    <span className="text-xs text-rose-500">{formErrors.firstName}</span>
                  )}
                </label>
                <label className="flex flex-col gap-2 font-semibold text-slate-600">
                  Last name *
                  <input
                    type="text"
                    className={`rounded-2xl border bg-slate-50 px-4 py-3 text-slate-700 focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/20 ${
                      formErrors.lastName ? 'border-rose-300' : 'border-slate-200'
                    }`}
                    value={formData.lastName}
                    onChange={(e) => setFormData((prev) => ({ ...prev, lastName: e.target.value }))}
                  />
                  {formErrors.lastName && (
                    <span className="text-xs text-rose-500">{formErrors.lastName}</span>
                  )}
                </label>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <label className="flex flex-col gap-2 font-semibold text-slate-600">
                  Username *
                  <input
                    type="text"
                    className={`rounded-2xl border bg-slate-50 px-4 py-3 text-slate-700 focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/20 ${
                      formErrors.username ? 'border-rose-300' : 'border-slate-200'
                    }`}
                    value={formData.username}
                    onChange={(e) => setFormData((prev) => ({ ...prev, username: e.target.value }))}
                  />
                  {formErrors.username && (
                    <span className="text-xs text-rose-500">{formErrors.username}</span>
                  )}
                </label>
                <label className="flex flex-col gap-2 font-semibold text-slate-600">
                  Email *
                  <input
                    type="email"
                    className={`rounded-2xl border bg-slate-50 px-4 py-3 text-slate-700 focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/20 ${
                      formErrors.email ? 'border-rose-300' : 'border-slate-200'
                    }`}
                    value={formData.email}
                    onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
                  />
                  {formErrors.email && <span className="text-xs text-rose-500">{formErrors.email}</span>}
                </label>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <label className="flex flex-col gap-2 font-semibold text-slate-600">
                  Phone *
                  <input
                    type="tel"
                    className={`rounded-2xl border bg-slate-50 px-4 py-3 text-slate-700 focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/20 ${
                      formErrors.phone ? 'border-rose-300' : 'border-slate-200'
                    }`}
                    value={formData.phone}
                    onChange={(e) => setFormData((prev) => ({ ...prev, phone: e.target.value }))}
                  />
                  {formErrors.phone && <span className="text-xs text-rose-500">{formErrors.phone}</span>}
                </label>
                <label className="flex flex-col gap-2 font-semibold text-slate-600">
                  Role *
                  <select
                    className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-slate-700 focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/20"
                    value={formData.role}
                    onChange={(e) => setFormData((prev) => ({ ...prev, role: e.target.value as User['role'] }))}
                  >
                    {roles.map((role) => (
                      <option key={role.value} value={role.value}>
                        {role.label}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <label className="flex flex-col gap-2 font-semibold text-slate-600">
                  Password *
                  <input
                    type="password"
                    className={`rounded-2xl border bg-slate-50 px-4 py-3 text-slate-700 focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/20 ${
                      formErrors.password ? 'border-rose-300' : 'border-slate-200'
                    }`}
                    value={formData.password}
                    onChange={(e) => setFormData((prev) => ({ ...prev, password: e.target.value }))}
                  />
                  {formErrors.password && (
                    <span className="text-xs text-rose-500">{formErrors.password}</span>
                  )}
                </label>
                <label className="flex flex-col gap-2 font-semibold text-slate-600">
                  Confirm password *
                  <input
                    type="password"
                    className={`rounded-2xl border bg-slate-50 px-4 py-3 text-slate-700 focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/20 ${
                      formErrors.confirmPassword ? 'border-rose-300' : 'border-slate-200'
                    }`}
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData((prev) => ({ ...prev, confirmPassword: e.target.value }))}
                  />
                  {formErrors.confirmPassword && (
                    <span className="text-xs text-rose-500">{formErrors.confirmPassword}</span>
                  )}
                </label>
              </div>

              <div className="flex flex-col gap-3 pt-4 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  className="inline-flex items-center justify-center rounded-full border border-slate-200 px-5 py-2.5 text-sm font-semibold text-slate-600 transition hover:bg-slate-100"
                  onClick={() => setShowForm(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="inline-flex items-center justify-center rounded-full bg-brand-primary px-6 py-2.5 text-sm font-semibold text-white shadow-soft transition hover:bg-brand-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-accent"
                >
                  Send invite
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;
